import {Component, OnInit, ViewChild} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Router} from '@angular/router';
import {catchError, forkJoin, Observable, of} from 'rxjs';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {AppRoutes} from '../../../enums/routes';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';
import {FormsGateHttpService} from '../../forms/data-access/forms-gate-http.service';
import {FormHttpService} from '../../forms/data-access/form-http.service';
import {SubmissionHttpService} from '../../forms/data-access/submission-http.service';
import {FormDefinition} from '../../forms/shared/models/form-schema.model';
import {Submission, SubmissionStatus} from '../../forms/shared/models/submission.model';
import {FormsGateStatus, GateVerdict} from '../../forms/shared/models/forms-gate-status.model';
import {
  buildSubmitRequest, maskedFieldKeys, maskedFieldMap, storedFieldKeys, submissionToInitialValue,
  withoutHiddenFiles
} from '../../forms/shared/services/submission-mapper';
import {readSubmissionFieldErrors} from '../../forms/shared/services/submission-errors';
import {DynamicFormComponent} from '../../forms/runtime/components/dynamic-form/dynamic-form.component';
import {SelectedFilesState} from '../../forms/runtime/services/selected-files-state.service';
import {MaskedFieldsState} from '../../forms/runtime/services/masked-fields-state.service';

/**
 * What a form on this screen is asking of the employee. It decides both the wording and the **write path**:
 * a first fill creates a submission, a re-confirmation revises the existing one.
 */
export type GateTaskKind =
  /** Required, never submitted, and blocking (policy `Immediate`, or a backfill window that has elapsed). */
  | 'missing'
  /** Required, never submitted, still inside its backfill Frist — fillable voluntarily, not blocking. */
  | 'pending'
  /** Completed, but the form's Gültigkeitsdauer has elapsed → "abgelaufen", re-confirm the answers. */
  | 'expired'
  /** Completed, but flagged by a reviewer or a changed definition → "geändert", re-check the answers. */
  | 'review';

/** One form to deal with on this screen, plus everything needed to write it back. */
export interface GateTask {
  /** The form key (`FormDocument.Key`). */
  readonly key: string;
  readonly kind: GateTaskKind;
  readonly definition: FormDefinition;
  /** The Frist this task runs against (backfill window or review deadline), or null when it already blocks. */
  readonly deadline: string | null;
  /** The finalised submission to revise — set only for `expired` / `review`, and never guessed (server-supplied). */
  readonly reviseFrom: string | null;
  /** The draft being edited: an adopted draft, or the revision draft created when the wizard reaches this form. */
  draftId: string | null;
  /** Previous answers to re-confirm, prefilled into the runtime form. Empty for a first fill. */
  initialValue: any;
  /**
   * Masked sensitive fields carried by the revision, addressed by `(groupKey, index, fieldKey)` — untouched
   * ones are sent as `keep`. Addressed rather than flat since #482, so two rows of a list cannot collide.
   */
  masked: Record<string, unknown>;
  /** The same fields as BARE keys, for the renderer's edit gate, which gates per field rather than per row. */
  maskedKeys: ReadonlySet<string>;
  /**
   * What the revision draft already stores. A field that stopped applying is CLEARED rather than omitted, because
   * on an update an omitted key means "leave the stored value alone" (#470). Empty for a first fill.
   */
  stored: ReadonlySet<string>;
  /** True once the write target exists (nothing to do for a first fill; the revision draft for a re-confirmation). */
  prepared: boolean;
}

/**
 * A required form this screen knows about but cannot offer, because its **definition** could not be loaded
 * (#502). It is listed rather than dropped: the gate still counts the form, so hiding it would leave the
 * employee working through a wizard that can never satisfy the gate, with no hint why.
 */
export interface UnavailableForm {
  /** The form key (`FormDocument.Key`) — the only identity available, since the title lives in the definition. */
  readonly key: string;
  /** Why it was on the list, so the wording can stay truthful about what is being asked. */
  readonly kind: GateTaskKind;
}

/**
 * Forms-only completion screen for a session the required-forms gate has marked <c>restricted</c>: the employee
 * cannot reach the rest of the panel until the required forms are dealt with. It loads the form definitions
 * (from {@code /auth/forms-gate/status}), renders them one at a time via {@link DynamicFormComponent}, writes each
 * to the Forms service, and — once all are in — re-issues the session token live (no re-login) and lands the
 * employee in the panel. See docs/articles/forms/required-forms-gating.md.
 *
 * **Attachments (#258).** File fields are part of the wizard here (unlike the revision editor, which strips them
 * into a separate panel), because a first fill has no submission id yet and therefore no upload endpoint to call.
 * The picked files are collected by {@link SelectedFilesState} and sent **with** the submit as one multipart
 * request, so a required file field is satisfiable and nothing half-finished can ever satisfy the gate.
 *
 * **Pending forms (#404).** The same screen doubles as the *voluntary* fill surface for a form that was assigned
 * with the `Grace` backfill policy and is still inside its 14-day Frist. Those arrive in the gate status's
 * `pending` list — deliberately **not** in `missingFormKeys` — so they never enter a restricted session's blocking
 * set, and the pending mode offers a way back into the panel instead of trapping the employee.
 *
 * **Flagged and expired forms (#404 — the lockout this screen used to create).** A stale answer (an elapsed
 * Gültigkeitsdauer, or a reviewer/definition flag) is reported in `flagged[]`, never in `missingFormKeys` — the
 * answer exists, it is just no longer good. Past its 14-day grace the verdict is `Restricted`, and
 * `FormsGateMiddleware` then 403s every monolith route, which puts "Meine Formulare" → "Überarbeiten" — the only
 * self-service fix — out of reach. Building the list from `missingFormKeys` alone therefore produced a permanent
 * dead end ("Überprüfung ausstehend") that only a manager could lift. Those forms are now offered here too,
 * through the **existing revise flow** (`POST …/{id}/revise` → edit → `PUT` finalise): the original is superseded,
 * the flag is cleared and the fresh `SubmittedAt` starts a new validity window, so one reissue frees the session.
 */
@Component({
  selector: 'app-forms-completion',
  standalone: true,
  imports: [DatePipe, DynamicFormComponent],
  // Scopes the picked-files side-channel to this screen, so the field-renderer publishes into it (see
  // SelectedFilesState) and a builder preview elsewhere stays unaffected. MaskedFieldsState does the same for a
  // re-confirmation: the carried masked values render as a mask with an explicit edit affordance, exactly as in
  // the revision editor, so an untouched sensitive answer is preserved rather than overwritten with its mask.
  providers: [SelectedFilesState, MaskedFieldsState],
  templateUrl: './forms-completion.component.html'
})
export class FormsCompletionComponent implements OnInit {
  /** Loading the gate status / definitions, opening a revision, or finalising — shows the spinner. */
  loading = true;
  /** A submit/reissue is in flight. */
  submitting = false;
  /** A "save draft" is in flight. */
  savingDraft = false;
  /** The Forms service was unreachable (fail-soft) — surfaced as a hint. */
  unavailable = false;
  /**
   * Restricted with nothing the employee can self-resolve. Since #404's revise path this is the narrow safety
   * net only: a flag with no revisable submission behind it (or an older server that reports none).
   */
  blockedNoAction = false;
  errorMessage: string | null = null;

  /**
   * True when the listed forms **block** the session. False in the voluntary mode: forms still inside their Frist
   * (a backfill grace, or a flag that is not yet overdue), where the session is fully usable and the screen offers
   * a way back to the panel.
   */
  blocking = true;
  /** The earliest Frist of the listed forms, shown on the intro card. Null while {@link blocking}. */
  pendingDeadline: string | null = null;

  /** The forms to deal with this session — the blocking set, or the voluntary one. */
  tasks: GateTask[] = [];
  currentIndex = 0;

  /**
   * Required forms whose definition failed to load while others succeeded (#502). This and the load failure in
   * {@link errorMessage} are mutually exclusive: some-failed and all-failed are two different screens.
   */
  unavailableForms: UnavailableForm[] = [];

  /** The live runtime form, so a server 422 can be pushed back onto the offending controls. */
  @ViewChild(DynamicFormComponent) private dynamicForm?: DynamicFormComponent;

  constructor(
    private gate: FormsGateHttpService,
    private formHttp: FormHttpService,
    private submissionHttp: SubmissionHttpService,
    private auth: AuthenticationService,
    private alert: GlobalAlertService,
    private router: Router,
    private selectedFiles: SelectedFilesState,
    private maskedFields: MaskedFieldsState
  ) {
  }

  ngOnInit(): void {
    this.refresh();
  }

  /** The task being worked on, or null (loading / nothing to do). */
  get currentTask(): GateTask | null {
    return this.tasks[this.currentIndex] ?? null;
  }

  get current(): FormDefinition | null {
    return this.currentTask?.definition ?? null;
  }

  /** The previous answers prefilled into the runtime form — empty for a first fill. */
  get currentInitialValue(): any {
    return this.currentTask?.initialValue ?? {};
  }

  /** The definitions on the screen, in wizard order. */
  get forms(): FormDefinition[] {
    return this.tasks.map(t => t.definition);
  }

  get total(): number {
    return this.tasks.length;
  }

  /** True when at least one listed form has never been filled in — decides the voluntary intro's wording. */
  get hasNewForms(): boolean {
    return this.tasks.some(t => t.kind === 'missing' || t.kind === 'pending');
  }

  /** True when some required forms loaded and others did not — the partial-failure state (#502). */
  get hasUnavailableForms(): boolean {
    return this.unavailableForms.length > 0;
  }

  /**
   * What a form that could not be loaded was being asked for. Only the key is known (the title lives in the
   * definition that failed), so this keeps the line truthful about *why* the form is still outstanding.
   */
  unavailableKindLabel(kind: GateTaskKind): string {
    switch (kind) {
      case 'expired':
        return 'Gültigkeit abgelaufen';
      case 'review':
        return 'Formular geändert';
      case 'pending':
        return 'Neu zugewiesen';
      default:
        return 'Noch nicht ausgefüllt';
    }
  }

  /** Re-reads the gate status and rebuilds the list of forms to deal with. */
  refresh(): void {
    this.loading = true;
    this.errorMessage = null;
    this.blockedNoAction = false;
    // A retry re-attempts every definition, the failed ones included, so the warning must not outlive it.
    this.unavailableForms = [];

    this.gate.status().subscribe({
      next: status => {
        this.unavailable = status.unavailable;
        const specs = this.plan(status);

        // Nothing blocking and nothing to offer ⇒ the panel is where the employee belongs. (A `review` session
        // may legitimately stand here — the guard lets it through — so this is the bounce, with the live gate
        // status the token claim lacks.)
        if (specs.length === 0 && !this.blocking) {
          this.leaveToPanel();
          return;
        }

        if (specs.length === 0) {
          // Blocking with nothing offerable: an overdue flag whose submission we cannot revise. Rare, and the
          // only remaining case where a human has to step in.
          this.blockedNoAction = true;
          this.loading = false;
          return;
        }

        this.pendingDeadline = this.blocking ? null : earliestDeadline(specs.map(s => s.deadline));
        this.loadTasks(specs);
      },
      error: () => this.fail('Der Formular-Status konnte nicht geladen werden.')
    });
  }

  /**
   * Turns a gate status into the forms this screen offers, and sets {@link blocking}.
   *
   * Restricted ⇒ exactly what blocks: `missingFormKeys` plus the **overdue** flags (the two things
   * `FormGateEvaluator` restricts on). A flag that is still inside its grace waits — it does not lengthen a
   * lockout wizard, and the panel is reachable again the moment the blocking ones are done.
   * Not restricted ⇒ everything that is open, offered voluntarily: pending backfills and not-yet-overdue flags.
   */
  private plan(status: FormsGateStatus): TaskSpec[] {
    this.blocking = status.verdict === GateVerdict.Restricted;
    const flagged = (status.flagged ?? []).filter(f => f.overdue === this.blocking);

    const specs: TaskSpec[] = this.blocking
      ? (status.missingFormKeys ?? []).map(key => ({key, kind: 'missing' as const, deadline: null, reviseFrom: null}))
      : (status.pending ?? []).map(p => ({key: p.formKey, kind: 'pending' as const, deadline: p.deadline, reviseFrom: null}));

    for (const f of flagged) {
      // No submission to revise ⇒ not self-serviceable; leave it out rather than offering an empty wizard step.
      if (!f.submissionId) continue;
      specs.push({
        key: f.formKey,
        kind: f.expired ? 'expired' : 'review',
        deadline: f.deadline ?? null,
        reviseFrom: f.submissionId
      });
    }

    return specs;
  }

  /**
   * Load the definitions for the planned forms, adopt any existing draft, and prepare the first one.
   *
   * **One dead form must not kill the whole screen (#502).** This used to be a plain
   * `forkJoin(specs.map(s => getDefinition(s.key)))`, which fails as a unit: a single 404 — a form deleted after
   * it was required — discarded every definition that had loaded fine and left the employee on a bare
   * "Die erforderlichen Formulare konnten nicht geladen werden." That is the user-visible half of a login
   * lockout, because the gate confines the session to this screen and this screen then offers nothing. Each
   * request therefore catches its own failure into a `null` sentinel, so the join always completes and the
   * outcome is decided per form:
   *
   * - **Some fail** → the resolved tasks render as usual, with a warning region above them naming the forms that
   *   could not be loaded. The employee makes progress on what works, and is told plainly that the rest is still
   *   required — silently dropping them would leave a wizard that can never satisfy the gate.
   * - **All fail** → the full-screen error state with "Erneut versuchen" stays, because there is nothing to
   *   render and a warning hovering over an empty list is worse than a clear failure.
   *
   * This path is **belt-and-braces, not the primary fix**: the Forms service no longer reports a requirement
   * whose form is gone, so a deleted form never reaches here any more. What survives is the unforeseen case — a
   * transient 500, a permissions edge, a form unreachable for some reason nobody enumerated — and for those the
   * screen must degrade rather than lock out.
   *
   * The submission list is deliberately **not** made fail-soft the same way: without it a started draft is not
   * adopted and the finalise creates a duplicate submission instead of updating it, so failing loudly there is
   * the honest outcome.
   */
  private loadTasks(specs: TaskSpec[]): void {
    forkJoin({
      // `null` is the per-request sentinel — see the doc comment. `of(null)` completes immediately, so one
      // failure costs the join nothing but that slot.
      defs: forkJoin(specs.map(s => this.formHttp.getDefinition(s.key).pipe(catchError(() => of(null))))),
      submissions: this.submissionHttp.list()
    }).subscribe({
      next: ({defs, submissions}) => {
        const loadable = specs.filter((_, i) => defs[i] !== null);

        // Nothing loaded ⇒ the full-screen error state, exactly as before.
        if (loadable.length === 0) {
          this.tasks = [];
          this.unavailableForms = [];
          this.fail('Die erforderlichen Formulare konnten nicht geladen werden.');
          return;
        }

        this.unavailableForms = specs
          .filter((_, i) => defs[i] === null)
          .map(spec => ({key: spec.key, kind: spec.kind}));

        // Adopt any existing draft for a listed form, so saving/finalising updates that draft
        // (resume-and-continue) instead of creating a duplicate — and so a re-confirmation the employee already
        // started is picked up rather than revising the same submission a second time.
        const drafts: Record<string, string> = {};
        for (const s of submissions) {
          if (s.status === SubmissionStatus.Draft && loadable.some(spec => spec.key === s.formId)) {
            drafts[s.formId] = s.id;
          }
        }

        this.tasks = specs.flatMap((spec, i) => {
          const definition = defs[i];
          if (definition === null) return [];
          return [{
            ...spec,
            definition,
            draftId: drafts[spec.key] ?? null,
            initialValue: {},
            masked: {},
            maskedKeys: new Set<string>(),
            stored: new Set<string>(),
            // A first fill has no write target to create up front; a re-confirmation does, opened lazily.
            prepared: spec.reviseFrom === null
          }];
        });
        this.currentIndex = 0;
        this.prepareCurrent();
      },
      error: () => this.fail('Die erforderlichen Formulare konnten nicht geladen werden.')
    });
  }

  /**
   * Make the current task writable. A first fill needs nothing; a re-confirmation needs its **draft**: either the
   * one the employee already started, or a fresh revision of the stale answer. Done lazily, when the wizard
   * reaches the form, so nothing is minted for a form the employee may never get to.
   */
  private prepareCurrent(): void {
    const task = this.currentTask;
    // `prepared` covers a first fill; the second condition is belt-and-braces for a re-confirmation with
    // neither a draft nor a submission behind it — there would be nothing to open, so treat it as ready.
    if (!task || task.prepared || (!task.draftId && !task.reviseFrom)) {
      if (task) task.prepared = true;
      this.applyMaskedState();
      this.loading = false;
      return;
    }

    this.loading = true;
    // An adopted draft already IS the editable clone — reading it is both cheaper and idempotent; only a task
    // with no draft yet starts a new revision.
    const source: Observable<Submission> = task.draftId
      ? this.submissionHttp.get(task.draftId)
      : this.submissionHttp.revise(task.reviseFrom!);

    source.subscribe({
      next: draft => {
        task.draftId = draft.id;
        task.initialValue = submissionToInitialValue(task.definition, draft);
        task.masked = maskedFieldMap(draft);
        task.maskedKeys = maskedFieldKeys(draft);
        task.stored = storedFieldKeys(draft);
        task.prepared = true;
        this.applyMaskedState();
        this.loading = false;
      },
      error: () => this.fail('Das Formular konnte nicht zur Überarbeitung geöffnet werden. Bitte versuchen Sie es erneut.')
    });
  }

  /** Edit-gate exactly the masked fields of the form on screen (none for a first fill). */
  private applyMaskedState(): void {
    this.maskedFields.set(this.currentTask?.maskedKeys ?? new Set<string>());
  }

  /** Leave the screen without finishing — only offered in the voluntary mode, never while blocking. */
  postpone(): void {
    if (this.blocking) return;
    this.leaveToPanel();
  }

  /** Handler for a {@link DynamicFormComponent} submit: finalise the form, then advance or finalise the gate. */
  onFormSubmit(rawValue: any): void {
    const task = this.currentTask;
    if (!task || this.submitting) return;

    this.submitting = true;
    this.errorMessage = null;

    this.persist(task, rawValue, true).subscribe({
      next: () => {
        this.submitting = false;
        // The files that belonged to this form are stored now; the next form starts with a clean side-channel.
        this.selectedFiles.reset();
        this.advanceOrFinish();
      },
      error: err => {
        this.submitting = false;
        this.errorMessage = this.describeFailure(err);
      }
    });
  }

  /**
   * Turns a failed submit into something the employee can act on. A `422` carries per-field failures
   * (`{fieldKey, code, message}`): those are pushed back onto the controls so the offending field is marked with
   * its own message, and the banner is reduced to whatever belonged to no field. Before #258 the whole payload
   * was discarded for one generic sentence, which left a required file field permanently unfixable.
   */
  private describeFailure(err: unknown): string {
    const fieldErrors = readSubmissionFieldErrors(err);
    if (fieldErrors.length === 0) {
      return (err as {status?: number} | null)?.status === 422
        ? 'Bitte überprüfen Sie Ihre Angaben — einige Pflichtfelder fehlen oder sind ungültig.'
        : 'Das Formular konnte nicht gesendet werden. Bitte versuchen Sie es erneut.';
    }

    const unmatched = this.dynamicForm?.applyServerErrors(fieldErrors) ?? fieldErrors;
    return unmatched.length > 0
      ? unmatched.map(e => e.message).join(' ')
      : 'Bitte korrigieren Sie die rot markierten Felder.';
  }

  /** Handler for a {@link DynamicFormComponent} "save draft": persist as a draft (no validation), to resume later. */
  onSaveDraft(rawValue: any): void {
    const task = this.currentTask;
    if (!task || this.submitting || this.savingDraft) return;

    this.savingDraft = true;
    this.persist(task, rawValue, false).subscribe({
      next: saved => {
        task.draftId = saved.id;
        this.savingDraft = false;
        this.alert.createAndShow('Entwurf gespeichert', 'Sie können später hier weitermachen.', AlertLevel.success, 2500);
      },
      error: () => {
        this.savingDraft = false;
        this.alert.createAndShow('Fehler', 'Der Entwurf konnte nicht gespeichert werden.', AlertLevel.error, 3000);
      }
    });
  }

  logout(): void {
    this.auth.logout();
  }

  /**
   * Create or update the submission for a task: updates the draft (an adopted one, or the revision opened for a
   * re-confirmation) when there is one, else creates a new submission. {@code finalize} controls draft vs. final.
   *
   * Untouched **masked** sensitive fields of a revision are sent as `keep`, so their stored value is preserved —
   * the same contract the revision editor honours. Any file the employee picked rides along in the same request
   * (multipart) rather than being uploaded separately, so the submit is atomic — see #258.
   */
  private persist(task: GateTask, rawValue: any, finalize: boolean): Observable<Submission> {
    const request = buildSubmitRequest(task.definition, rawValue, finalize, {masked: task.masked, stored: task.stored});
    // An attachment for a question that no longer applies must not be transmitted (#494). Filtered here
    // rather than purged from SelectedFilesState, so re-showing the question restores the pick.
    const files = withoutHiddenFiles(task.definition, rawValue, this.selectedFiles.snapshot());
    return task.draftId
      ? this.submissionHttp.update(task.draftId, request, files)
      : this.submissionHttp.submit(request, files);
  }

  private advanceOrFinish(): void {
    if (this.currentIndex < this.tasks.length - 1) {
      this.currentIndex++;
      this.prepareCurrent();
      return;
    }
    this.finalize();
  }

  /** All forms dealt with → re-issue the token and re-evaluate the gate. */
  private finalize(): void {
    this.loading = true;
    this.gate.reissue().subscribe({
      next: token => {
        this.auth.applyReissuedSession(token);
        // Still restricted (e.g. a relationship-inherited requirement surfaced) → reload the remaining set.
        if (this.auth.isFormsRestricted) this.refresh();
        else this.leaveToPanel();
      },
      error: () => this.fail('Die Sitzung konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.')
    });
  }

  private leaveToPanel(): void {
    this.router.navigate(['/' + AppRoutes.EmployeePanel]).then();
  }

  private fail(message: string): void {
    this.loading = false;
    this.errorMessage = message;
  }
}

/** A planned task before its definition (and, for a re-confirmation, its draft) has been loaded. */
interface TaskSpec {
  key: string;
  kind: GateTaskKind;
  deadline: string | null;
  reviseFrom: string | null;
}

/** The earliest of a set of ISO deadlines, or null when there are none (missing/invalid dates are ignored). */
function earliestDeadline(deadlines: (string | null)[]): string | null {
  const times = deadlines
    .filter((d): d is string => !!d)
    .map(d => new Date(d).getTime())
    .filter(t => Number.isFinite(t));
  return times.length ? new Date(Math.min(...times)).toISOString() : null;
}
